import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Loader2, Play } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import DaySelector from './DaySelector';
import FlexEScenarioBuilder from './FlexEScenarioBuilder';

interface FlexESimulationFormProps {
  uploadId: string;
  onSimulationComplete: (simId: string) => void;
}

export default function FlexESimulationForm({ 
  uploadId, 
  onSimulationComplete 
}: FlexESimulationFormProps) {
  const [running, setRunning] = useState(false);
  const { toast } = useToast();

  // Form state
  const [simName, setSimName] = useState('');
  const [capLimitedKw, setCapLimitedKw] = useState(135);
  const [battKw, setBattKw] = useState(100);
  const [cValue, setCValue] = useState(0.5);
  const [windowStart, setWindowStart] = useState('16:00');
  const [windowEnd, setWindowEnd] = useState('21:00');
  const [daysMask, setDaysMask] = useState('1-7');
  const [scenarioConfig, setScenarioConfig] = useState<any>(null);

  // Calculated values
  const [battKwh, setBattKwh] = useState(0);

  useEffect(() => {
    if (battKw > 0 && cValue > 0) {
      setBattKwh(battKw / cValue);
    }
  }, [battKw, cValue]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation - days
    if (!daysMask || daysMask === '') {
      toast({
        title: 'Geen dagen geselecteerd',
        description: 'Selecteer minimaal één dag voor de simulatie',
        variant: 'destructive'
      });
      return;
    }

    // Validation - C-value
    if (cValue <= 0 || cValue > 10) {
      toast({
        title: 'Ongeldige C-waarde',
        description: 'C-waarde moet tussen 0.1 en 10 liggen',
        variant: 'destructive'
      });
      return;
    }

    const [startH, startM] = windowStart.split(':').map(Number);
    const [endH, endM] = windowEnd.split(':').map(Number);
    if ((endH * 60 + endM) <= (startH * 60 + startM)) {
      toast({
        title: 'Ongeldige tijden',
        description: 'Eindtijd moet na starttijd liggen',
        variant: 'destructive'
      });
      return;
    }

    setRunning(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('run-flexe-simulation', {
        body: {
          upload_id: uploadId,
          name: simName || 'Simulatie',
          cap_limited_kw: capLimitedKw,
          batt_kw: battKw,
          c_value: cValue,
          window_start: windowStart,
          window_end: windowEnd,
          days_mask: daysMask,
          scenario_config: scenarioConfig || undefined
        }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Simulation failed');

      toast({
        title: 'Simulatie voltooid',
        description: `${data.passed_days}/${data.total_days} dagen geslaagd (${data.success_rate.toFixed(1)}%)`
      });

      onSimulationComplete(data.sim_id);

    } catch (error: any) {
      console.error('Simulation error:', error);
      
      let errorTitle = 'Simulatie mislukt';
      let errorMessage = 'Er is een fout opgetreden';
      
      // Parse specific error codes from edge function
      if (error.message?.includes('STORAGE_PATH_MISSING')) {
        errorTitle = 'Upload incompleet';
        errorMessage = 'De upload is niet correct afgerond. Upload het bestand opnieuw.';
      } else if (error.message?.includes('UPLOAD_NOT_FOUND')) {
        errorTitle = 'Upload niet gevonden';
        errorMessage = 'De upload kan niet worden gevonden. Upload het bestand opnieuw.';
      } else if (error.message?.includes('STORAGE_READ_FAILED')) {
        errorTitle = 'Kan data niet laden';
        errorMessage = 'Er is een fout opgetreden bij het laden van de data uit storage.';
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.error) {
        errorMessage = error.error;
      }
      
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Scenario Builder */}
      <FlexEScenarioBuilder
        uploadId={uploadId}
        onScenarioChange={setScenarioConfig}
      />

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">2️⃣</span>
          <h2 className="text-xl font-semibold">Simulatie-instellingen</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
        {/* Simulation Name */}
        <div className="space-y-2">
          <Label htmlFor="sim_name">
            Simulatienaam (optioneel)
          </Label>
          <Input
            id="sim_name"
            type="text"
            value={simName}
            onChange={(e) => setSimName(e.target.value)}
            placeholder="Bijvoorbeeld: Batterij XL Test"
            maxLength={100}
          />
          <p className="text-xs text-muted-foreground">
            Geef deze simulatie een herkenbare naam voor later terug te vinden
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Contract Capacity */}
          <div className="space-y-2">
            <Label htmlFor="cap_limited_kw">
              Contractvermogen in beperking (kW)
            </Label>
            <Input
              id="cap_limited_kw"
              type="number"
              value={capLimitedKw}
              onChange={(e) => setCapLimitedKw(Number(e.target.value))}
              min={0}
              step={1}
              required
            />
          </div>

          {/* Battery Power */}
          <div className="space-y-2">
            <Label htmlFor="batt_kw">
              Batterijvermogen (kW)
            </Label>
            <Input
              id="batt_kw"
              type="number"
              value={battKw}
              onChange={(e) => setBattKw(Number(e.target.value))}
              min={0}
              step={1}
              required
            />
          </div>

          {/* C-Value */}
          <div className="space-y-2">
            <Label htmlFor="c_value">
              C-waarde
            </Label>
            <Input
              id="c_value"
              type="number"
              value={cValue}
              onChange={(e) => setCValue(Number(e.target.value))}
              min={0.1}
              max={10}
              step={0.1}
              required
            />
            <p className="text-xs text-muted-foreground">
              Typisch: 0.5 (conservatief) tot 1.0 (agressief)
            </p>
          </div>

          {/* Window Start */}
          <div className="space-y-2">
            <Label htmlFor="window_start">
              Starttijd beperking
            </Label>
            <Input
              id="window_start"
              type="time"
              value={windowStart}
              onChange={(e) => setWindowStart(e.target.value)}
              required
            />
          </div>

          {/* Window End */}
          <div className="space-y-2">
            <Label htmlFor="window_end">
              Eindtijd beperking
            </Label>
            <Input
              id="window_end"
              type="time"
              value={windowEnd}
              onChange={(e) => setWindowEnd(e.target.value)}
              required
            />
          </div>
        </div>

        {/* Days Selector */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            Actieve dagen
            <span className="text-xs text-muted-foreground">
              ({daysMask === '1-7' ? 'Alle dagen' : 
                daysMask.split(',').filter(d => d).length === 0 ? 'Geen dagen geselecteerd' :
                `${daysMask.split(',').filter(d => d).length} dagen geselecteerd`})
            </span>
          </Label>
          <DaySelector value={daysMask} onChange={setDaysMask} />
          {daysMask === '' && (
            <p className="text-sm text-destructive mt-1">
              ⚠️ Selecteer minimaal één dag
            </p>
          )}
        </div>

        {/* Calculated Capacity */}
        <Alert>
          <AlertTitle>Berekende batterijcapaciteit</AlertTitle>
          <AlertDescription>
            {battKw} kW ÷ {cValue} = <strong>{battKwh.toFixed(1)} kWh</strong>
          </AlertDescription>
        </Alert>

        {/* Submit Button */}
        <Button 
          type="submit" 
          disabled={running || !daysMask || daysMask === ''} 
          className="w-full" 
          size="lg"
          title={!daysMask || daysMask === '' ? 'Selecteer minimaal één dag' : undefined}
        >
          {running ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Simuleren...
            </>
          ) : (
            <>
              <Play className="mr-2 h-4 w-4" />
              Start Simulatie
            </>
          )}
        </Button>
        {(!daysMask || daysMask === '') && (
          <p className="text-xs text-muted-foreground text-center -mt-2">
            Selecteer minimaal één dag om de simulatie te starten
          </p>
        )}
      </form>
    </Card>
    </div>
  );
}
